import { supabase } from '@/lib/supabase';
import type { Community, Profile } from '@/types';

/** The slice of a profile we need to render a friend tile. */
export type FriendProfile = Pick<Profile, 'id' | 'display_name' | 'avatar_url' | 'handle'>;
export interface Friendship {
  created_at: string;
}

export interface FriendRequest {
  id: string;
  requester_id: string;
  recipient_id: string;
  status: 'pending' | 'accepted' | 'denied';
  requested_at: string;
  requester: FriendProfile | null;
}

/**
 * A user's friends. The `friends` table is symmetric (one row per pair,
 * `user_id_1`/`user_id_2`), so we match either column and resolve the *other*
 * id to a profile. Two round-trips (edges → profiles) keeps it independent of
 * the generated FK relationships, which the `friends` row doesn't declare.
 */
export async function listFriends(userId: string): Promise<FriendProfile[]> {
  const { data: edges, error } = await supabase
    .from('friends')
    .select('user_id_1, user_id_2')
    .or(`user_id_1.eq.${userId},user_id_2.eq.${userId}`);
  if (error) throw error;

  const otherIds = (edges ?? []).map((e) => (e.user_id_1 === userId ? e.user_id_2 : e.user_id_1));
  if (otherIds.length === 0) return [];

  const { data: profiles, error: pErr } = await supabase
    .from('profiles')
    .select('id, display_name, avatar_url, handle')
    .in('id', otherIds);
  if (pErr) throw pErr;
  return profiles ?? [];
}

/** Find members by handle or display name (for the inbox "add friends" search). */
export async function searchProfiles(query: string, excludeId?: string): Promise<FriendProfile[]> {
  const safe = query.trim().replace(/[,()*%\\]/g, '');
  if (safe.length < 2) return [];
  const like = `%${safe}%`;
  const { data, error } = await supabase
    .from('profiles')
    .select('id, display_name, avatar_url, handle')
    .or(`handle.ilike.${like},display_name.ilike.${like}`)
    .limit(12);
  if (error) throw error;
  return (data ?? []).filter((r) => r.id !== excludeId);
}

/** Which of the given user ids are currently marked `online` in `user_presence`. */
export async function listOnlineUserIds(ids: string[]): Promise<string[]> {
  if (!ids.length) return [];
  const { data } = await supabase
    .from('user_presence')
    .select('user_id')
    .in('user_id', ids)
    .eq('status', 'online');
  return (data ?? []).map((r) => r.user_id);
}

export async function listMutualFriends(userId: string, otherUserId: string): Promise<FriendProfile[]> {
  const [mine, theirs] = await Promise.all([listFriends(userId), listFriends(otherUserId)]);
  const theirIds = new Set(theirs.map((f) => f.id));
  return mine.filter((f) => theirIds.has(f.id));
}

export async function getFriendship(userId: string, otherUserId: string): Promise<Friendship | null> {
  const pairA = `and(user_id_1.eq.${userId},user_id_2.eq.${otherUserId})`;
  const pairB = `and(user_id_1.eq.${otherUserId},user_id_2.eq.${userId})`;
  const { data, error } = await supabase
    .from('friends')
    .select('created_at')
    .or(`${pairA},${pairB}`)
    .maybeSingle();
  if (error) throw error;
  return data;
}

/** Whether the current user has a still-pending outgoing friend request to the
 *  other user (drives the "Request sent" state until they accept). */
export async function getSentFriendRequest(userId: string, otherUserId: string): Promise<boolean> {
  const { data, error } = await supabase
    .from('friend_requests')
    .select('id')
    .eq('requester_id', userId)
    .eq('recipient_id', otherUserId)
    .eq('status', 'pending')
    .maybeSingle();
  if (isMissingFriendRequestsTable(error)) return false;
  if (error) throw error;
  return Boolean(data);
}

export async function addFriend(userId: string, otherUserId: string): Promise<void> {
  const pair = [userId, otherUserId].sort();
  const user_id_1 = pair[0]!;
  const user_id_2 = pair[1]!;
  const { error } = await supabase.from('friends').insert({ user_id_1, user_id_2 });
  if (error && error.code !== '23505') throw error;
}

/** Remove a friendship (symmetric row, either ordering). */
export async function removeFriend(userId: string, otherUserId: string): Promise<void> {
  const { error } = await supabase
    .from('friends')
    .delete()
    .or(
      `and(user_id_1.eq.${userId},user_id_2.eq.${otherUserId}),and(user_id_1.eq.${otherUserId},user_id_2.eq.${userId})`,
    );
  if (error) throw error;
}

/** Block a user: record the block and drop any existing friendship. */
export async function blockUser(userId: string, otherUserId: string): Promise<void> {
  const { error } = await supabase
    .from('blocked_users')
    .insert({ blocker_id: userId, blocked_id: otherUserId });
  if (error && error.code !== '23505') throw error;
  await removeFriend(userId, otherUserId);
}

export async function sendFriendRequest(userId: string, otherUserId: string): Promise<void> {
  const { data: existingFriend, error: friendError } = await supabase
    .from('friends')
    .select('created_at')
    .or(
      `and(user_id_1.eq.${userId},user_id_2.eq.${otherUserId}),and(user_id_1.eq.${otherUserId},user_id_2.eq.${userId})`,
    )
    .maybeSingle();
  if (friendError) throw friendError;
  if (existingFriend) return;

  const { error } = await supabase
    .from('friend_requests')
    .insert({ requester_id: userId, recipient_id: otherUserId, status: 'pending' });
  if (isMissingFriendRequestsTable(error)) {
    throw new Error('Friend requests need the latest Supabase migration. Run 20260621020000_friend_requests.sql.');
  }
  if (error && error.code !== '23505') throw error;
}

export async function listIncomingFriendRequests(userId: string): Promise<FriendRequest[]> {
  const { data, error } = await supabase
    .from('friend_requests')
    .select('id, requester_id, recipient_id, status, requested_at')
    .eq('recipient_id', userId)
    .eq('status', 'pending')
    .order('requested_at', { ascending: false });
  if (isMissingFriendRequestsTable(error)) return [];
  if (error) throw error;
  const rows = (data ?? []) as unknown as Omit<FriendRequest, 'requester'>[];
  const requesterIds = [...new Set(rows.map((r) => r.requester_id))];
  const { data: profiles, error: profileError } = requesterIds.length
    ? await supabase.from('profiles').select('id, display_name, avatar_url, handle').in('id', requesterIds)
    : { data: [], error: null };
  if (profileError) throw profileError;
  const byId = new Map((profiles ?? []).map((p) => [p.id, p]));
  return rows.map((r) => ({ ...r, requester: byId.get(r.requester_id) ?? null }));
}

export async function acceptFriendRequest(request: FriendRequest): Promise<void> {
  await addFriend(request.requester_id, request.recipient_id);
  const { error } = await supabase
    .from('friend_requests')
    .update({ status: 'accepted', responded_at: new Date().toISOString() })
    .eq('id', request.id);
  if (isMissingFriendRequestsTable(error)) {
    throw new Error('Friend requests need the latest Supabase migration. Run 20260621020000_friend_requests.sql.');
  }
  if (error) throw error;
}

export async function denyFriendRequest(id: string): Promise<void> {
  const { error } = await supabase
    .from('friend_requests')
    .update({ status: 'denied', responded_at: new Date().toISOString() })
    .eq('id', id);
  if (isMissingFriendRequestsTable(error)) {
    throw new Error('Friend requests need the latest Supabase migration. Run 20260621020000_friend_requests.sql.');
  }
  if (error) throw error;
}

function isMissingFriendRequestsTable(error: { code?: string; message?: string } | null): boolean {
  return Boolean(
    error &&
      (error.code === '42P01' ||
        error.code === 'PGRST205' ||
        /friend_requests|schema cache|does not exist/i.test(error.message ?? '')),
  );
}

export interface ActivityItem {
  id: string;
  action_text: string;
  created_at: string;
  click_action: string | null;
  actor: FriendProfile | null;
  community: { id: string; name: string } | null;
}

/**
 * Community notifications feed (`activity_feed`). RLS already scopes rows to the
 * viewer's network — their communities + friends. We drop the viewer's own
 * actions (you don't notify yourself) and resolve actor profiles + community
 * names for display.
 */
export async function listActivityFeed(userId: string): Promise<ActivityItem[]> {
  const { data, error } = await supabase
    .from('activity_feed')
    .select('id, actor_id, action_text, community_id, click_action, created_at')
    .neq('actor_id', userId)
    .order('created_at', { ascending: false })
    .limit(20);
  if (error) throw error;
  const rows = data ?? [];
  if (!rows.length) return [];

  const actorIds = [...new Set(rows.map((r) => r.actor_id))];
  const communityIds = [...new Set(rows.map((r) => r.community_id).filter((id): id is string => Boolean(id)))];
  const [profiles, communities] = await Promise.all([
    supabase.from('profiles').select('id, display_name, avatar_url, handle').in('id', actorIds),
    communityIds.length
      ? supabase.from('communities').select('id, name').in('id', communityIds)
      : Promise.resolve({ data: [] as { id: string; name: string }[] }),
  ]);
  const actorById = new Map((profiles.data ?? []).map((p) => [p.id, p]));
  const communityById = new Map((communities.data ?? []).map((c) => [c.id, c]));

  return rows.map((r) => ({
    id: r.id,
    action_text: r.action_text,
    created_at: r.created_at,
    click_action: r.click_action,
    actor: actorById.get(r.actor_id) ?? null,
    community: r.community_id ? (communityById.get(r.community_id) ?? null) : null,
  }));
}

/** Communities a user belongs to (via `community_members`). */
export async function listUserCommunities(userId: string): Promise<Community[]> {
  const { data: memberships, error } = await supabase
    .from('community_members')
    .select('community_id')
    .eq('user_id', userId);
  if (error) throw error;

  const ids = (memberships ?? []).map((m) => m.community_id);
  if (ids.length === 0) return [];

  const { data, error: cErr } = await supabase.from('communities').select('*').in('id', ids);
  if (cErr) throw cErr;
  return data ?? [];
}
